// 11/03 (Josh): the checkout cart — pure helpers behind SyndicationDisbursements.js.
// A cart is {position_id: amountString}. The server re-validates every line against live availability;
// these helpers only keep the screen honest (totals, over-limit flags, ready-to-checkout).

export const cents = (v) => Math.round((Number(v) || 0) * 100) / 100;
export const money = (v) => (v == null || !Number.isFinite(Number(v)))
  ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Is the typed amount a positive whole-cent figure? */
export const validAmount = (s) => /^\d+(\.\d{1,2})?$/.test(String(s ?? '').trim()) && Number(s) > 0;

/** Toggle a position in the cart: on → prefilled with its full available, off → removed. */
export function toggleLine(cart, position) {
  const next = { ...cart };
  if (position.position_id in next) delete next[position.position_id];
  else next[position.position_id] = position.available > 0 ? position.available.toFixed(2) : '';
  return next;
}

export function setLineAmount(cart, positionId, value) {
  return { ...cart, [positionId]: value };
}

/** Select every position with money available, each at its full available amount. */
export function selectAllAvailable(positions) {
  const next = {};
  positions.forEach(p => { if (p.available > 0) next[p.position_id] = p.available.toFixed(2); });
  return next;
}

/** Per-line status + total. A line is `over` when it exceeds the position's available, `invalid` when not a number. */
export function cartSummary(cart, positions) {
  const byId = Object.fromEntries(positions.map(p => [p.position_id, p]));
  const lines = Object.entries(cart).map(([position_id, raw]) => {
    const p = byId[position_id];
    const amount = validAmount(raw) ? cents(raw) : null;
    const over = amount != null && p && amount > cents(p.available) + 1e-9;
    return { position_id, deal_id: p?.deal_id, deal_number: p?.deal_number, deal_name: p?.deal_name,
      available: p?.available, amount, raw, invalid: amount == null, over: !!over };
  });
  const total = cents(lines.reduce((s, l) => s + (l.amount || 0), 0));
  const problems = lines.filter(l => l.invalid || l.over).length;
  return { lines, total, count: lines.length, problems, ready: lines.length > 0 && problems === 0 };
}

/** The request body for POST /disbursements/checkout. */
export function checkoutBody(cart, positions, { syndicatorId, method, reference, note, paidOn, requestId, dryRun = false }) {
  const { lines } = cartSummary(cart, positions);
  return {
    syndicator_id: syndicatorId,
    lines: lines.map(l => ({ position_id: l.position_id, deal_id: l.deal_id, amount: l.amount })),
    method, reference: reference || '', note: note || '', paid_on: paidOn || null,
    request_id: requestId || null, dry_run: dryRun,
  };
}

/** Pre-fill the cart from a syndicator's withdrawal request: per-deal lines when it carries them, else the
 *  requested amount spread over the positions with money, largest available first. Never above a position's available. */
export function cartFromRequest(request, positions) {
  const cart = {};
  if (!request) return cart;
  const byDeal = {};
  positions.forEach(p => { (byDeal[p.deal_id] = byDeal[p.deal_id] || []).push(p); });
  if (Array.isArray(request.lines) && request.lines.length) {
    request.lines.forEach(l => {
      const ps = byDeal[l.deal_id] || [];
      if (ps.length === 1) cart[ps[0].position_id] = cents(Math.min(l.amount, ps[0].available)).toFixed(2);
    });
    return cart;
  }
  let left = cents(request.amount);
  [...positions].filter(p => p.available > 0).sort((a, b) => b.available - a.available).forEach(p => {
    if (left <= 0) return;
    const take = cents(Math.min(left, p.available));
    if (take > 0) { cart[p.position_id] = take.toFixed(2); left = cents(left - take); }
  });
  return cart;
}

export const METHOD_LABELS = { wire: 'Wire', ach: 'ACH', zelle: 'Zelle', check: 'Check', mca_deduction: 'MCA Suite deduction', other: 'Other' };

/** History rows → CSV (what the admin's "Download CSV" button saves when no server call is wanted). */
export function historyCsv(rows) {
  const esc = v => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const out = [['Date', 'Syndicator', 'Deal', 'Merchant', 'Amount', 'Disbursement total', 'Method', 'Reference', 'Status', 'Note'].join(',')];
  rows.forEach(d => d.lines.forEach(l => out.push([d.paid_on, d.syndicator_name, l.deal_number, l.deal_name, l.amount.toFixed(2),
    d.total.toFixed(2), METHOD_LABELS[d.method] || d.method, d.reference || '', d.status, d.note || ''].map(esc).join(','))));
  return out.join('\n');
}
